import type { ContentRun, SiteContentPage } from '../../types';

type Props = {
  pages: SiteContentPage[];
  lastRun: ContentRun | null;
  hasBrief: boolean;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

/** Model + DataForSEO spend per page, from the pipeline's own telemetry across
 *  12 real runs. Mirrors the server's estimate; the server's number is the one
 *  that gets recorded, this is only what the dialog shows. */
const USD_PER_PAGE = 0.31;

function daysSince(iso: string | null): number | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / 86_400_000);
}

/**
 * Confirmation before a run is queued.
 *
 * Exists because a refresh spends real money and the annual cadence means the
 * honest answer to "should I click this?" is usually no. Three things decide
 * it, so all three are on screen: how many pages, roughly what it costs, and
 * how recently this franchisee was last done.
 */
export default function RefreshConfirm({
  pages, lastRun, hasBrief, busy, onConfirm, onCancel,
}: Props) {
  const n = pages.length;
  const cost = (n * USD_PER_PAGE).toFixed(2);
  const since = daysSince(lastRun?.finishedAt ?? null);
  const recent = since !== null && since < 30;

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-label="Confirm content refresh">
      <div className="modal-card">
        <h3>Refresh content?</h3>

        <p>
          This will redraft <strong>{n} page{n === 1 ? '' : 's'}</strong> at an estimated{' '}
          <strong>${cost}</strong>.
        </p>

        {/* The two things most likely to make this click a mistake. */}
        {recent && (
          <div className="schema-warning">
            This franchisee was last refreshed{' '}
            <strong>{since === 0 ? 'today' : `${since} day${since === 1 ? '' : 's'} ago`}</strong>.
            Running again will redraft the same pages and bill for it.
          </div>
        )}
        {!hasBrief && (
          <div className="schema-warning">
            No territory brief has been submitted for this franchisee. The pages will be
            drafted without the owner's own angle, which is the main thing that makes them
            different from every other franchise's. Consider chasing the brief first.
          </div>
        )}

        <p className="field-help">
          Nothing publishes automatically. The run produces draft content for review.
        </p>

        <div className="content-actions">
          <button type="button" className="secondary-btn" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button type="button" onClick={onConfirm} disabled={busy || n === 0}>
            {busy ? 'Queueing…' : `Refresh ${n} page${n === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>
  );
}
